'use client';

/**
 * InboxThreePane — the single cross-job recruiter CRM inbox (spec §9).
 *
 * Three panes: filters/list | thread timeline | context. Lives OUTSIDE job
 * workflows — a candidate spans jobs/campaigns but has one thread. The default
 * view is the needs-action queue (unanswered replies + escalations) sorted
 * needs-attention → recency; "All inbox" shows everything.
 *
 * The thread is one chronological cross-channel timeline (email + LinkedIn),
 * with channel markers and the AI classification on inbound replies. When a
 * thread spans channels, the composer's channel choice also focuses the
 * timeline on that channel's history (tabs above the thread switch it back).
 * Sending a manual reply stops the candidate's automation server-side (§6).
 *
 * The three panes are resizable: drag the dividers to grow the list/context
 * panes (widths persist per browser). Keyboard (progressive enhancement): j/k
 * move the list selection, r focuses the composer. Quiet surfaces; coral
 * reserved for Send.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defaultRangeExtractor, useVirtualizer, type Range } from '@tanstack/react-virtual';
import {
    Search,
    Mail,
    Inbox as InboxIcon,
    AlertCircle,
    AlertTriangle,
    RefreshCw,
    Building2,
    Megaphone,
    User as UserIcon,
    Clock,
    ShieldAlert,
    Loader2,
    X,
    ArrowLeft,
    ChevronRight,
    Layers,
    CheckCircle2,
    MapPin,
    Phone,
    Globe,
    Activity,
} from 'lucide-react';
import { Avatar, Badge, type BadgeVariant } from '@/components/ui-v2';
import { inboxApi, campaignsApi, enrichmentApi } from '../services/outreach.api';
import { refreshOutreachCounts } from '../useOutreachCounts';
import { jobApi } from '@/modules/job/services/job.api';
import { VerificationBadge } from './VerificationBadge';
import {
    INBOX_FILTERS,
    REPLY_CLASS_META,
    CONVERSATION_STATUS_LABELS,
    INTEREST_META,
    INTEREST_ORDER,
} from '../constants';
import type {
    InboxFilter,
    InboxItem,
    InboxCandidate,
    ThreadResponse,
    ThreadEnrollment,
    ThreadActivityEntry,
    ThreadMessage,
    ReplyAccounts,
    ReplyPayload,
    StepChannel,
    AccountProvider,
    InterestLevel,
    EmailVerification,
} from '../types';
import { ReplyComposer, type ReplyComposerHandle } from './ReplyComposer';
import { looksLikeHtml, sanitizeOutboundHtml } from '../rich-text';
import { showErrorToast } from '@/services/toastService';
import { getInboxIcon, getInboxMessageIcon } from './inbox-icons';
import { useAuth } from '@/auth/AuthProvider';
import {
    INBOX_PAGE_SIZE,
    appendInboxPage,
    getInboxSnapshotForScope,
    inboxFeedCacheKey,
    inboxSnapshotFromPage,
    isFreshInboxSnapshot,
    putInboxSnapshot,
    rebuildInboxSnapshotToDepth,
    refreshInboxHead,
    type InboxFeedSnapshot,
} from '../inbox-feed-cache';
import { buildInboxListRows } from '../inbox-list-rows';

/* ─── helpers ─── */

function relativeTime(iso?: string | null): string {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const s = Math.floor((Date.now() - then) / 1000);
    if (s < 60) return 'now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d`;
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function initialsOf(name?: string): string {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
}

// Aging signal for the needs-action worklist: how long the candidate's reply
// has been waiting on a human. Goes "warm" past a day — that's the lead cooling.
const WAITING_WARN_MS = 24 * 60 * 60 * 1000;

function waitingFor(item: Pick<InboxItem, 'needsReply' | 'lastInboundAt'>): { label: string; warn: boolean } | null {
    if (!item.needsReply || !item.lastInboundAt) return null;
    const since = Date.now() - new Date(item.lastInboundAt).getTime();
    if (Number.isNaN(since) || since < 60 * 1000) return null;
    return { label: `waiting ${relativeTime(item.lastInboundAt)}`, warn: since > WAITING_WARN_MS };
}

const CHANNEL_ICON: Record<StepChannel, typeof Mail> = {
    email: getInboxIcon('email'),
    linkedin: getInboxIcon('linkedin'),
    whatsapp: getInboxIcon('whatsapp'),
};
const CHANNEL_FILTER_LABEL: Record<'all' | StepChannel, string> = {
    all: 'All',
    email: 'Email',
    linkedin: 'LinkedIn',
    whatsapp: 'WhatsApp',
};


// Live-inbox poll cadence (10s, while the tab is active). No SSE/websocket — K8s
// pods are stateless, so an in-memory push bus can't fan out across them; polling
// is the portable mechanism until we add serverless-Redis pub/sub. Inbound is
// drained by the SQS worker within ~1s, so this only bounds how quickly a
// processed reply surfaces in the UI — not delivery.
const INBOX_POLL_MS = 10_000;

// Is a scroll container within a few lines of its bottom? Gates auto-scroll on a
// background refresh: jump to a freshly-arrived message only when the recruiter
// is already reading the latest, never when they've scrolled up into history.
function isNearBottom(el: HTMLElement, threshold = 140): boolean {
    return el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
}

// Scoped styles for a rendered HTML email reply in the timeline. Kept minimal and
// token-based so an outbound message inherits the inbox's typography (rather than
// pulling in the editor's content stylesheet) while still showing lists, links,
// quotes, and bold/italic the recruiter applied.
function ensureInboxHtmlStyles(): void {
    if (typeof document === 'undefined') return;
    const id = 'ot-inbox-msg-html';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
        .ot-msg-html { font-size: 13px; line-height: 1.6; color: var(--text-primary); word-break: break-word; overflow-wrap: anywhere; }
        .ot-msg-html > :first-child { margin-top: 0; }
        .ot-msg-html > :last-child { margin-bottom: 0; }
        .ot-msg-html p { margin: 0 0 0.5em; }
        .ot-msg-html ul, .ot-msg-html ol { margin: 0 0 0.5em; padding-left: 1.25em; }
        .ot-msg-html li { margin: 0.1em 0; }
        .ot-msg-html a { color: var(--accent-emphasis); text-decoration: underline; }
        .ot-msg-html strong, .ot-msg-html b { font-weight: 600; }
        .ot-msg-html blockquote { margin: 0 0 0.5em; padding-left: 0.75em; border-left: 2px solid var(--border-default); color: var(--text-secondary); }
    `;
    document.head.appendChild(style);
}

/** One message body in the timeline. Outbound email replies authored in the rich
 *  editor are HTML — render them (sanitized) so the recruiter sees the formatting
 *  they sent. Everything else renders as plain text: inbound is already cleaned to
 *  text server-side (displayBody), and LinkedIn/WhatsApp are plain-text channels. */
function MessageBody({ message }: { message: ThreadMessage }) {
    const body = message.body || '';
    if (message.direction === 'out' && looksLikeHtml(body)) {
        // First-party recruiter HTML, sanitized to an allowlist before render;
        // inbound provider HTML never reaches this path.
        return <div className="ot-msg-html" dangerouslySetInnerHTML={{ __html: sanitizeOutboundHtml(body) }} />;
    }
    return (
        <p
            className="text-[13px] leading-relaxed whitespace-pre-wrap break-words"
            style={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}
        >
            {body || '—'}
        </p>
    );
}

/* ─── resizable panes ─── */

const clampWidth = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** A pane width that survives reloads (per browser). Resize is delta-based so
 *  the divider can stream pointer movements straight in. */
function usePersistedWidth(key: string, initial: number, min: number, max: number) {
    const [width, setWidth] = useState<number>(() => {
        if (typeof window === 'undefined') return initial;
        const saved = Number(window.localStorage.getItem(key));
        return Number.isFinite(saved) && saved > 0 ? clampWidth(saved, min, max) : initial;
    });
    const resizeBy = useCallback((dx: number) => setWidth((w) => clampWidth(w + dx, min, max)), [min, max]);
    useEffect(() => {
        try {
            window.localStorage.setItem(key, String(width));
        } catch {
            /* private mode etc. — width just won't persist */
        }
    }, [key, width]);
    return [width, resizeBy] as const;
}

/** Invisible drag strip between panes — overlays the pane border, shows a
 *  coral grip on hover, and streams pointer deltas to the owner. */
function PaneDivider({ onDrag, ariaLabel }: { onDrag: (dx: number) => void; ariaLabel: string }) {
    return (
        <div
            role="separator"
            aria-orientation="vertical"
            aria-label={ariaLabel}
            className="group relative z-[2] -mx-[3px] w-[7px] shrink-0 cursor-col-resize"
            onPointerDown={(e) => {
                e.preventDefault();
                const el = e.currentTarget;
                try {
                    el.setPointerCapture(e.pointerId);
                } catch {
                    /* capture is an enhancement — the drag still tracks the strip */
                }
                let lastX = e.clientX;
                const move = (ev: PointerEvent) => {
                    onDrag(ev.clientX - lastX);
                    lastX = ev.clientX;
                };
                const up = () => {
                    el.removeEventListener('pointermove', move);
                    el.removeEventListener('pointerup', up);
                };
                el.addEventListener('pointermove', move);
                el.addEventListener('pointerup', up);
            }}
        >
            <div
                className="absolute inset-y-0 left-1/2 w-[3px] -translate-x-1/2 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
                style={{ background: 'var(--accent)' }}
            />
        </div>
    );
}

export interface InboxThreePaneProps {
    initialThreadId?: string | null;
    onThreadChange?: (id: string | null) => void;
    /** Scope the inbox to one campaign's replies (§9 deep-link, e.g. from setup). */
    scopeCampaignId?: string | null;
    /** Scope the inbox to one role's replies (e.g. from Analytics drill-down). */
    scopeJobId?: string | null;
    /** Clear the campaign/job scope and return to the full cross-job inbox. */
    onClearScope?: () => void;
    /** Set/clear the campaign scope from the in-inbox campaign picker (?campaign=). */
    onSelectCampaign?: (campaignId: string | null) => void;
}

export function InboxThreePane({
    initialThreadId,
    onThreadChange,
    scopeCampaignId,
    scopeJobId,
    onClearScope,
    onSelectCampaign,
}: InboxThreePaneProps) {
    const { organization, currentWorkspaceId } = useAuth();
    const [filter, setFilter] = useState<InboxFilter>('all');
    const [channel, setChannel] = useState<StepChannel | 'all'>('all');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [scopeLabel, setScopeLabel] = useState<string | null>(null);

    // Resizable layout: list/context pane widths persist per browser.
    const [listWidth, resizeList] = usePersistedWidth('ot.inbox.listWidth', 340, 264, 560);
    const [contextWidth, resizeContext] = usePersistedWidth('ot.inbox.contextWidth', 300, 244, 520);

    // List organization: a campaign picker (drives ?campaign=) + a group-by-campaign
    // toggle that breaks the list into collapsible sections (§9).
    const [campaigns, setCampaigns] = useState<{ _id: string; name: string }[]>([]);
    const [groupByCampaign, setGroupByCampaign] = useState(false);

    // Grouping-by-campaign is only meaningful across many campaigns; when the list
    // is scoped to one, force it off so a stale toggle doesn't leave a lone group.
    useEffect(() => {
        if (scopeCampaignId) setGroupByCampaign(false);
    }, [scopeCampaignId]);
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());

    const toggleGroup = useCallback((id: string) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const [items, setItems] = useState<InboxItem[]>([]);
    const [listTotal, setListTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [feedStale, setFeedStale] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
    const [listStatus, setListStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
    const [listError, setListError] = useState<string | null>(null);

    const [selectedId, setSelectedId] = useState<string | null>(initialThreadId || null);
    const [thread, setThread] = useState<ThreadResponse | null>(null);
    const [threadStatus, setThreadStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>('idle');
    const [threadError, setThreadError] = useState<string | null>(null);

    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const composerRef = useRef<ReplyComposerHandle>(null);
    const timelineEndRef = useRef<HTMLDivElement>(null);
    const timelineScrollRef = useRef<HTMLDivElement>(null);
    const listScrollRef = useRef<HTMLDivElement>(null);
    const feedCacheRef = useRef<Map<string, InboxFeedSnapshot>>(new Map());
    const feedSnapshotRef = useRef<InboxFeedSnapshot | null>(null);
    const activeFeedKeyRef = useRef('');
    const listRequestIdRef = useRef(0);
    const loadingMoreRequestRef = useRef<string | null>(null);
    // Mirrors for the background poller — set once so the interval reads the
    // latest without re-subscribing: the selected thread, its last-seen message
    // count (to detect growth), and a one-shot gate so only a user-initiated load
    // may auto-pick a thread (a poll must never yank the recruiter off the one
    // they're reading).
    const selectedIdRef = useRef<string | null>(initialThreadId || null);
    const threadMsgCountRef = useRef(0);
    const allowAutoSelectRef = useRef(false);

    // Multi-channel threads: the composer's channel (null = thread default) and
    // which channel's history the timeline shows. Choosing a channel in the
    // composer focuses the timeline on that channel; tabs switch it back.
    const [replyChannel, setReplyChannel] = useState<StepChannel | null>(null);
    const [timelineChannel, setTimelineChannel] = useState<StepChannel | 'all'>('all');

    /* ─── load list ─── */
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), search ? 300 : 0);
        return () => clearTimeout(timer);
    }, [search]);

    const feedKey = useMemo(
        () =>
            inboxFeedCacheKey({
                organizationId: organization?._id,
                workspaceId: currentWorkspaceId,
                filter,
                channel: channel === 'all' ? null : channel,
                search: debouncedSearch,
                campaignId: scopeCampaignId,
                jobId: scopeJobId,
            }),
        [organization?._id, currentWorkspaceId, filter, channel, debouncedSearch, scopeCampaignId, scopeJobId],
    );

    const fetchInbox = useCallback(async (cursor?: string, limit = INBOX_PAGE_SIZE) => {
        const { data } = await inboxApi.list({
            filter,
            channel: channel === 'all' ? undefined : channel,
            search: debouncedSearch || undefined,
            campaignId: scopeCampaignId || undefined,
            jobId: scopeJobId || undefined,
            limit,
            cursor,
        });
        return data;
    }, [filter, channel, debouncedSearch, scopeCampaignId, scopeJobId]);

    const commitFeedSnapshot = useCallback(
        (key: string, snapshot: InboxFeedSnapshot, restoreScroll = false) => {
            putInboxSnapshot(feedCacheRef.current, key, snapshot);
            if (activeFeedKeyRef.current !== key) return;
            feedSnapshotRef.current = snapshot;
            setItems(snapshot.items);
            setListTotal(snapshot.total);
            setHasMore(snapshot.hasMore);
            setFeedStale(snapshot.stale);
            if (restoreScroll) {
                requestAnimationFrame(() => {
                    if (activeFeedKeyRef.current === key && listScrollRef.current) {
                        listScrollRef.current.scrollTop = snapshot.scrollTop;
                    }
                });
            }
        },
        [],
    );

    // Full load: drives the skeleton/error chrome and lets the auto-select effect
    // (re)pick a thread. Used on first load and every filter/scope change.
    const loadInbox = useCallback(async () => {
        const key = feedKey;
        const requestId = ++listRequestIdRef.current;
        allowAutoSelectRef.current = true;
        setListStatus('loading');
        setListError(null);
        setLoadMoreError(null);
        try {
            const data = await fetchInbox();
            if (activeFeedKeyRef.current !== key || requestId !== listRequestIdRef.current) return;
            // A stale cached scope may have already consumed the one-shot
            // auto-select gate while this request was in flight. Re-open it for
            // the authoritative page so a row that left the filter is replaced.
            allowAutoSelectRef.current = true;
            commitFeedSnapshot(key, inboxSnapshotFromPage(data));
            setListStatus('loaded');
            requestAnimationFrame(() => {
                if (activeFeedKeyRef.current === key && listScrollRef.current) {
                    listScrollRef.current.scrollTop = 0;
                }
            });
        } catch (err) {
            if (activeFeedKeyRef.current !== key || requestId !== listRequestIdRef.current) return;
            const e = err as { response?: { data?: { error?: string } }; message?: string };
            setListError(e?.response?.data?.error || e?.message || 'Failed to load inbox');
            setListStatus('error');
        }
    }, [feedKey, fetchInbox, commitFeedSnapshot]);

    // A reply changes the feed generation and may reorder/remove its thread.
    // Rebuild the already loaded window from page one in that single generation
    // so grouped/scrolled inboxes do not collapse back to 50 rows.
    const reloadInboxToCurrentDepth = useCallback(async () => {
        const key = activeFeedKeyRef.current;
        const current = feedSnapshotRef.current;
        if (!key || !current) {
            await loadInbox();
            return;
        }

        const targetCount = Math.max(INBOX_PAGE_SIZE, current.items.length);
        const requestId = ++listRequestIdRef.current;
        loadingMoreRequestRef.current = null;
        setIsLoadingMore(false);
        setLoadMoreError(null);

        try {
            const next = await rebuildInboxSnapshotToDepth(
                async (cursor, limit) => {
                    if (activeFeedKeyRef.current !== key || requestId !== listRequestIdRef.current) {
                        throw new Error('Inbox reload superseded');
                    }
                    return fetchInbox(cursor, limit);
                },
                targetCount,
                {
                    scrollTop: current.scrollTop,
                    maxAttempts: 3,
                    shouldRetry: (error) => {
                        const response = (error as {
                            response?: { status?: number; data?: { details?: { reason?: string } } };
                        })?.response;
                        return response?.status === 409 && response.data?.details?.reason === 'inbox_changed';
                    },
                },
            );
            if (activeFeedKeyRef.current !== key || requestId !== listRequestIdRef.current) return;

            const selectedStillLoaded = Boolean(
                selectedIdRef.current && next.items.some((item) => item._id === selectedIdRef.current),
            );
            const restored = selectedStillLoaded ? next : { ...next, scrollTop: 0 };
            allowAutoSelectRef.current = !selectedStillLoaded;
            commitFeedSnapshot(key, restored, true);
            setListStatus('loaded');
        } catch (error) {
            if (activeFeedKeyRef.current !== key || requestId !== listRequestIdRef.current) return;
            const latest = feedSnapshotRef.current;
            if (latest) commitFeedSnapshot(key, { ...latest, stale: true });
            const e = error as { response?: { data?: { error?: string } }; message?: string };
            setLoadMoreError(e?.response?.data?.error || e?.message || 'Could not refresh conversations');
        }
    }, [fetchInbox, loadInbox, commitFeedSnapshot]);

    // Silent poll refresh. A channel tab derived from a complete All snapshot is
    // already exact, but has no server-issued channel generation; replace it
    // silently at the same depth instead of trying to merge incompatible feeds.
    const refreshInbox = useCallback(async () => {
        const key = activeFeedKeyRef.current;
        const current = feedSnapshotRef.current;
        if (!key || !current || current.stale) return;
        if (current.derivedFromCompleteScope) {
            void reloadInboxToCurrentDepth();
            return;
        }
        try {
            const data = await fetchInbox();
            if (activeFeedKeyRef.current !== key || feedSnapshotRef.current !== current) return;
            commitFeedSnapshot(key, refreshInboxHead(current, data));
        } catch {
            /* keep the last good list */
        }
    }, [fetchInbox, commitFeedSnapshot, reloadInboxToCurrentDepth]);

    const loadMoreInbox = useCallback(async () => {
        const key = activeFeedKeyRef.current;
        const current = feedSnapshotRef.current;
        const cursor = current?.nextCursor;
        if (!key || current?.stale || !current?.hasMore || !cursor || loadingMoreRequestRef.current) return;

        const requestId = listRequestIdRef.current;
        const requestToken = `${key}:${requestId}:${cursor}`;
        loadingMoreRequestRef.current = requestToken;
        setIsLoadingMore(true);
        setLoadMoreError(null);
        try {
            const data = await fetchInbox(cursor);
            if (
                activeFeedKeyRef.current !== key ||
                requestId !== listRequestIdRef.current ||
                !feedSnapshotRef.current ||
                feedSnapshotRef.current.nextCursor !== cursor
            ) {
                return;
            }
            commitFeedSnapshot(key, appendInboxPage(feedSnapshotRef.current, data));
        } catch (err) {
            if (
                activeFeedKeyRef.current !== key ||
                requestId !== listRequestIdRef.current ||
                loadingMoreRequestRef.current !== requestToken
            ) {
                return;
            }
            const e = err as {
                response?: { status?: number; data?: { error?: string; details?: { reason?: string } } };
                message?: string;
            };
            if (e.response?.status === 409 && e.response.data?.details?.reason === 'inbox_changed') {
                const currentSnapshot = feedSnapshotRef.current;
                if (currentSnapshot) commitFeedSnapshot(key, { ...currentSnapshot, stale: true });
                return;
            }
            setLoadMoreError(e?.response?.data?.error || e?.message || 'Could not load more conversations');
        } finally {
            if (loadingMoreRequestRef.current === requestToken) {
                loadingMoreRequestRef.current = null;
                if (activeFeedKeyRef.current === key) setIsLoadingMore(false);
            }
        }
    }, [fetchInbox, commitFeedSnapshot]);

    const handleListScroll = useCallback(() => {
        const element = listScrollRef.current;
        const snapshot = feedSnapshotRef.current;
        const key = activeFeedKeyRef.current;
        if (!element || !snapshot || !key) return;

        const withScroll = { ...snapshot, scrollTop: element.scrollTop };
        feedSnapshotRef.current = withScroll;
        putInboxSnapshot(feedCacheRef.current, key, withScroll);
        if (element.scrollHeight - element.scrollTop - element.clientHeight < 480) void loadMoreInbox();
    }, [loadMoreInbox]);

    // Resolve a human label for the active scope banner (campaign name / role title).
    useEffect(() => {
        let cancelled = false;
        if (scopeCampaignId) {
            campaignsApi
                .get(scopeCampaignId)
                .then(({ data }) => {
                    if (!cancelled) setScopeLabel(data.campaign?.name || 'Campaign');
                })
                .catch(() => {
                    if (!cancelled) setScopeLabel('Campaign');
                });
        } else if (scopeJobId) {
            jobApi
                .getJob(scopeJobId)
                .then(({ data }) => {
                    if (!cancelled) setScopeLabel(data?.title || 'Role');
                })
                .catch(() => {
                    if (!cancelled) setScopeLabel('Role');
                });
        } else {
            setScopeLabel(null);
        }
        return () => {
            cancelled = true;
        };
    }, [scopeCampaignId, scopeJobId]);

    // Campaigns that power the campaign picker (drives ?campaign=).
    useEffect(() => {
        let cancelled = false;
        campaignsApi
            .list()
            .then(({ data }) => {
                if (!cancelled) setCampaigns((data.campaigns || []).map((c) => ({ _id: c._id, name: c.name })));
            })
            .catch(() => {
                /* picker simply stays empty if campaigns can't load */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const listRows = useMemo(
        () => buildInboxListRows(items, groupByCampaign, collapsedGroups),
        [items, groupByCampaign, collapsedGroups],
    );
    const campaignHeaderIndexes = useMemo(
        () => listRows.flatMap((row, index) => (row.kind === 'campaign' ? [index] : [])),
        [listRows],
    );
    const extractListRange = useCallback(
        (range: Range) => {
            const activeHeader = campaignHeaderIndexes.filter((index) => index <= range.startIndex).at(-1);
            const indexes = defaultRangeExtractor(range);
            return activeHeader == null ? indexes : [...new Set([activeHeader, ...indexes])];
        },
        [campaignHeaderIndexes],
    );
    const listVirtualizer = useVirtualizer({
        count: listRows.length,
        getScrollElement: () => listScrollRef.current,
        estimateSize: (index) => (listRows[index]?.kind === 'campaign' ? 33 : 82),
        overscan: 8,
        getItemKey: (index) => listRows[index]?.key || index,
        rangeExtractor: extractListRange,
    });
    const activeCampaignHeaderIndex = campaignHeaderIndexes
        .filter((index) => index <= (listVirtualizer.range?.startIndex ?? 0))
        .at(-1);

    // Each org/workspace/filter/search scope owns a small LRU snapshot. Returning
    // to a fresh scope restores its rows + scroll position immediately; a stale
    // scope keeps the last good rows visible while one authoritative head page
    // replaces it. Request ids and feed keys prevent cross-scope stale writes.
    useEffect(() => {
        activeFeedKeyRef.current = feedKey;
        ++listRequestIdRef.current;
        loadingMoreRequestRef.current = null;
        setIsLoadingMore(false);
        setLoadMoreError(null);
        allowAutoSelectRef.current = true;

        const cached = getInboxSnapshotForScope(feedCacheRef.current, {
            organizationId: organization?._id,
            workspaceId: currentWorkspaceId,
            filter,
            channel: channel === 'all' ? null : channel,
            search: debouncedSearch,
            campaignId: scopeCampaignId,
            jobId: scopeJobId,
        });
        let shouldLoad = true;
        let shouldRebuildDerived = false;
        if (cached) {
            commitFeedSnapshot(feedKey, cached, true);
            setListStatus('loaded');
            setListError(null);
            const fresh = isFreshInboxSnapshot(cached);
            shouldLoad = !fresh && !cached.derivedFromCompleteScope;
            shouldRebuildDerived = !fresh && Boolean(cached.derivedFromCompleteScope);
        } else {
            feedSnapshotRef.current = null;
            setItems([]);
            setListTotal(0);
            setHasMore(false);
            setFeedStale(false);
            setListStatus('loading');
            setListError(null);
        }

        if (shouldLoad) void loadInbox();
        else if (shouldRebuildDerived) void reloadInboxToCurrentDepth();
        return () => {
            ++listRequestIdRef.current;
            loadingMoreRequestRef.current = null;
            if (activeFeedKeyRef.current === feedKey) activeFeedKeyRef.current = '';
        };
    }, [
        feedKey,
        organization?._id,
        currentWorkspaceId,
        filter,
        channel,
        debouncedSearch,
        scopeCampaignId,
        scopeJobId,
        loadInbox,
        reloadInboxToCurrentDepth,
        commitFeedSnapshot,
    ]);

    /* ─── load thread ─── */
    // Full load: shows the loading/error state and always scrolls to the newest
    // message (thread switch, deep-link, retry, after a send).
    const loadThread = useCallback(async (id: string) => {
        setThreadStatus('loading');
        setThreadError(null);
        try {
            const { data } = await inboxApi.thread(id);
            if (selectedIdRef.current !== id) return;
            setThread(data);
            setThreadStatus('loaded');
            requestAnimationFrame(() => timelineEndRef.current?.scrollIntoView({ block: 'end' }));
        } catch (err) {
            if (selectedIdRef.current !== id) return;
            const e = err as { response?: { data?: { error?: string } }; message?: string };
            setThreadError(e?.response?.data?.error || e?.message || 'Failed to load thread');
            setThreadStatus('error');
        }
    }, []);

    // Silent refresh for the poller: pull the open thread's latest timeline in
    // place. Ignores a response that lost the race to a thread switch, keeps the
    // last good thread on a transient error, and only auto-scrolls when a new
    // message arrived AND the recruiter is already at the bottom (never steals
    // scroll while they read history or type a reply).
    const refreshThread = useCallback(async (id: string) => {
        try {
            const { data } = await inboxApi.thread(id);
            if (selectedIdRef.current !== id) return;
            const grew = (data.messages?.length ?? 0) > threadMsgCountRef.current;
            const nearBottom = !timelineScrollRef.current || isNearBottom(timelineScrollRef.current);
            setThread(data);
            if (grew && nearBottom) {
                requestAnimationFrame(() => timelineEndRef.current?.scrollIntoView({ block: 'end' }));
            }
        } catch {
            /* keep the last good thread */
        }
    }, []);

    // Keep the poller's mirrors in sync with render state.
    useEffect(() => {
        selectedIdRef.current = selectedId;
    }, [selectedId]);
    useEffect(() => {
        threadMsgCountRef.current = thread?.messages.length ?? 0;
    }, [thread]);

    // Scoped styles for rendered HTML email replies in the timeline (injected once).
    useEffect(() => {
        ensureInboxHtmlStyles();
    }, []);

    const selectThread = useCallback(
        (id: string | null) => {
            selectedIdRef.current = id;
            setSelectedId(id);
            setSendError(null);
            // Channel choices are per-thread — reset to the thread's defaults.
            setReplyChannel(null);
            setTimelineChannel('all');
            onThreadChange?.(id);
            if (id) loadThread(id);
            else setThread(null);
        },
        [loadThread, onThreadChange]
    );

    // Auto-select the first thread when none is chosen yet.
    useEffect(() => {
        if (listStatus !== 'loaded') return;
        if (selectedId && selectedId === initialThreadId) {
            // A deep link is an explicit selection even when the conversation is
            // beyond the currently loaded list window. Fetch it directly instead
            // of replacing it with the first visible row.
            allowAutoSelectRef.current = false;
            if (threadStatus === 'idle') loadThread(selectedId);
            return;
        }
        if (selectedId && items.some((i) => i._id === selectedId)) {
            // Deep-linked (?thread=) on first load: the id is valid but nothing
            // has fetched it yet.
            if (threadStatus === 'idle') loadThread(selectedId);
            return;
        }
        // Only (re)pick a thread right after a user-initiated load (first load,
        // filter/scope change, post-action). A background poll updates `items`
        // too, but must never pull the recruiter off a thread that has dropped
        // out of the current filter (e.g. one they just replied to).
        if (!allowAutoSelectRef.current) return;
        allowAutoSelectRef.current = false;
        if (initialThreadId && items.some((i) => i._id === initialThreadId)) {
            selectThread(initialThreadId);
        } else if (items.length) {
            selectThread(items[0]._id);
        } else {
            selectThread(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listStatus, items]);

    /* ─── keyboard: j/k navigate, r focus composer ─── */
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.key === 'j' || e.key === 'k') {
                if (!items.length) return;
                const idx = items.findIndex((i) => i._id === selectedId);
                const next = e.key === 'j' ? Math.min(idx + 1, items.length - 1) : Math.max(idx - 1, 0);
                if (items[next]) selectThread(items[next]._id);
            } else if (e.key === 'r') {
                e.preventDefault();
                composerRef.current?.focus();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [items, selectedId, selectThread]);

    /* ─── live updates: poll while the tab is active ─── */
    // No SSE/websocket: on stateless K8s pods an in-memory push bus can't fan out
    // across instances, so we poll. Every INBOX_POLL_MS (while the tab is visible)
    // and immediately on focus/visibility, refetch the list + open thread + the
    // needs-action badge. Refetch is the source of truth — the stale-guard +
    // near-bottom auto-scroll live in the refreshers, so a poll never steals scroll
    // or clobbers a thread switch. (Future realtime: serverless-Redis pub/sub.)
    useEffect(() => {
        const tick = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            void refreshInbox();
            refreshOutreachCounts(); // keep the needs-action badge in step
            if (selectedIdRef.current) void refreshThread(selectedIdRef.current);
        };
        const interval = setInterval(tick, INBOX_POLL_MS);
        const onFocus = () => tick();
        const onVisibility = () => {
            if (document.visibilityState === 'visible') tick();
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearInterval(interval);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [refreshInbox, refreshThread]);

    /* ─── actions ─── */
    const handleSend = async (payload: ReplyPayload) => {
        if (!selectedId) return;
        setSending(true);
        setSendError(null);
        try {
            await inboxApi.reply(selectedId, payload);
            await loadThread(selectedId);
            // Reflect cleared needs-reply / new activity in the list.
            void reloadInboxToCurrentDepth();
        } catch (err) {
            // Caps gate (H12): the account hit its safety limit — a single,
            // human-judged reply may go anyway after an explicit confirm. The
            // override only ever covers caps; suppression blocks stay hard.
            const e = err as {
                response?: { status?: number; data?: { error?: string; details?: { retryAt?: string; reason?: string } } };
                message?: string;
            };
            if (e?.response?.status === 429 && !payload.override) {
                const retryAt = e.response?.data?.details?.retryAt;
                const when = retryAt ? ` It frees up around ${new Date(retryAt).toLocaleTimeString()}.` : '';
                const goAnyway = window.confirm(
                    `This account is at its safety cap (protects it from provider bans).${when}\n\nSend this one reply anyway?`
                );
                if (goAnyway) {
                    setSending(false);
                    return handleSend({ ...payload, override: true });
                }
            }
            // Surface the failure: an inline banner above the composer + a toast, so a
            // refused/failed send (e.g. provider error) is never silently swallowed.
            const msg = e?.response?.data?.error || e?.message || 'Failed to send your reply. Please try again.';
            setSendError(msg);
            showErrorToast(msg);
            // Re-throw so the composer keeps the typed text / open draft for a retry.
            throw err instanceof Error ? err : new Error(msg);
        } finally {
            setSending(false);
        }
    };

    // AI reply assist — fetch a suggested draft for the thread's latest inbound.
    // Returns copy only; the recruiter reviews/edits in the composer before sending.
    const handleDraft = useCallback(
        async (ch: StepChannel) => {
            if (!selectedId) throw new Error('No conversation selected');
            const { data } = await inboxApi.draftReply(selectedId, { channel: ch });
            return { body: data.body };
        },
        [selectedId]
    );

    /* ─── triage: done / interest (§9) ─── */
    const handleDone = async () => {
        if (!selectedId) return;
        try {
            await inboxApi.markDone(selectedId);
            await loadThread(selectedId);
            // Done changes the feed generation and can remove this thread from
            // needs_action. Preserve the already loaded window just like Send.
            void reloadInboxToCurrentDepth();
        } catch (err) {
            const e = err as { response?: { data?: { error?: string } }; message?: string };
            showErrorToast(e?.response?.data?.error || e?.message || 'Failed to mark done');
        }
    };


    const threadChannels: StepChannel[] = useMemo(
        () => (thread?.conversation.channels?.length ? thread.conversation.channels : ['email']),
        [thread]
    );
    // The composer's effective channel: the recruiter's pick if the thread has
    // it, else the thread's primary channel.
    const activeReplyChannel: StepChannel =
        replyChannel && threadChannels.includes(replyChannel) ? replyChannel : threadChannels[0];

    // Reply gating (Stage 1): if this thread's sender for the active channel is
    // down (disconnected/error/on_hold), disable the composer and prompt reconnect
    // rather than letting the recruiter type a reply that 400s on send. The inbox is
    // workspace-wide, so we trust the server-provided per-channel account status
    // (the owner-scoped accounts store won't have a teammate's account).
    const activeReplyAccount = thread?.replyAccounts?.[activeReplyChannel];
    const replyChannelDown =
        !!activeReplyAccount?.status && !['connected', 'warming'].includes(activeReplyAccount.status);

    // Picking a channel in the composer also focuses the timeline on it (§9:
    // "reply on LinkedIn" should read the LinkedIn history).
    const handleComposerChannel = useCallback((ch: StepChannel) => {
        setReplyChannel(ch);
        setTimelineChannel(ch);
    }, []);
    // Manual replies are sent verbatim (no variable resolution), so the composer
    // only offers the booking-link insert when one of the thread's campaigns
    // actually has a real link configured.
    const bookingLink = useMemo(
        () => thread?.campaigns?.find((c) => c.bookingLink?.trim())?.bookingLink?.trim(),
        [thread]
    );

    // AI drafting is offered only when there's a candidate message to reply to and
    // the thread isn't escalated (§8: escalated/sensitive replies are human-only —
    // the server refuses to draft them too).
    const canDraft = useMemo(
        () => !!thread?.messages.some((m) => m.direction === 'in') && !thread?.conversation.escalated,
        [thread]
    );

    return (
        <div
            className="flex h-full min-h-0"
            style={{ background: 'var(--canvas-base)', fontFamily: 'var(--v2-font-sans)' }}
        >
            {/* ───────────── Pane 1: filters + list ───────────── */}
            <aside
                className="flex min-h-0 shrink-0 flex-col"
                style={{
                    width: listWidth,
                    borderRight: '1px solid var(--border-default)',
                    background: 'var(--surface-primary)',
                }}
            >
                {/* Scope banner — ONLY for a role/job deep-link (e.g. from Analytics).
                    Campaign scope is owned by the campaign picker below, which shows
                    the active campaign and clears via "All campaigns" — so we don't
                    also stack a redundant banner for it (that was the incoherence). */}
                {scopeJobId && (
                    <div
                        className="flex items-center gap-2 px-3 py-2.5"
                        style={{ borderBottom: '1px solid var(--border-muted)', background: 'var(--accent-muted)' }}
                    >
                        <button
                            type="button"
                            onClick={onClearScope}
                            className="flex h-6 items-center gap-1 rounded-md px-1.5 text-[12px] font-medium transition-colors"
                            style={{ color: 'var(--accent-emphasis)' }}
                            title="Show every campaign reply"
                        >
                            <ArrowLeft size={13} strokeWidth={2} />
                            All inboxes
                        </button>
                        <span style={{ color: 'var(--text-tertiary)' }}>·</span>
                        <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium" style={{ color: 'var(--text-primary)' }}>
                            Replies · {scopeLabel || '…'}
                        </span>
                    </div>
                )}

                {/* Search */}
                <div className="p-3" style={{ borderBottom: '1px solid var(--border-muted)' }}>
                    <div
                        className="flex h-9 items-center overflow-hidden rounded-lg"
                        style={{ border: '1px solid var(--border-default)', background: 'var(--surface-primary)' }}
                    >
                        <span className="pl-3">
                            <Search size={14} strokeWidth={1.75} style={{ color: 'var(--text-tertiary)' }} />
                        </span>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search candidates…"
                            className="h-full flex-1 bg-transparent px-2.5 text-[13px] outline-none"
                            style={{ color: 'var(--text-primary)' }}
                        />
                    </div>

                    {/* Filter chips */}
                    <div className="v2-scrollbar-hide mt-2.5 flex items-center gap-1.5 overflow-x-auto">
                        {INBOX_FILTERS.map((f) => {
                            const active = filter === f.id;
                            return (
                                <button
                                    key={f.id}
                                    onClick={() => setFilter(f.id)}
                                    className="flex h-7 shrink-0 items-center gap-1 rounded-full px-2.5 text-[12px] font-medium whitespace-nowrap transition-colors"
                                    style={{
                                        background: active ? 'var(--accent-muted)' : 'var(--surface-tertiary)',
                                        color: active ? 'var(--accent-emphasis)' : 'var(--text-secondary)',
                                        border: `1px solid ${active ? 'var(--border-accent)' : 'transparent'}`,
                                    }}
                                >
                                    {f.label}
                                </button>
                            );
                        })}
                    </div>

                    {/* Channel sub-filter. WhatsApp hidden for now — threads still
                        render if they exist; re-add 'whatsapp' here to restore. */}
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {(['all', 'email', 'linkedin'] as const).map((ch) => {
                            const active = channel === ch;
                            const Icon = ch === 'all' ? null : CHANNEL_ICON[ch];
                            return (
                                <button
                                    key={ch}
                                    onClick={() => setChannel(ch)}
                                    className="flex h-6 items-center gap-1 rounded-md px-2 text-[11px] font-medium capitalize transition-colors"
                                    style={{
                                        background: active ? 'var(--surface-active)' : 'transparent',
                                        color: active ? 'var(--text-primary)' : 'var(--text-tertiary)',
                                    }}
                                >
                                    {Icon && <Icon size={12} />}
                                    {CHANNEL_FILTER_LABEL[ch]}
                                </button>
                            );
                        })}
                    </div>

                    {/* Campaign picker (drives ?campaign=) + group toggle */}
                    {campaigns.length > 0 && (
                        <div className="mt-2.5 flex items-center gap-1.5">
                            <select
                                value={scopeCampaignId || ''}
                                onChange={(e) => onSelectCampaign?.(e.target.value || null)}
                                className="h-8 w-0 min-w-0 flex-1 rounded-lg px-2 text-[12px] font-medium outline-none"
                                style={{
                                    // Active-filter styling when scoped to a campaign, mirroring
                                    // the filter chips — the picker IS the scope indicator now.
                                    border: `1px solid ${scopeCampaignId ? 'var(--border-accent)' : 'var(--border-default)'}`,
                                    background: scopeCampaignId ? 'var(--accent-muted)' : 'var(--surface-primary)',
                                    color: scopeCampaignId ? 'var(--accent-emphasis)' : 'var(--text-primary)',
                                }}
                                title={scopeCampaignId ? 'Showing one campaign — pick “All campaigns” to clear' : 'Filter the inbox to one campaign'}
                            >
                                <option value="">All campaigns</option>
                                {campaigns.map((c) => (
                                    <option key={c._id} value={c._id}>
                                        {c.name}
                                    </option>
                                ))}
                            </select>
                            {/* Group-by-campaign only makes sense across MANY campaigns —
                                hide it when the list is already scoped to one. */}
                            {!scopeCampaignId && (
                                <button
                                    type="button"
                                    onClick={() => setGroupByCampaign((g) => !g)}
                                    className="flex h-8 shrink-0 items-center gap-1 rounded-lg px-2 text-[12px] font-medium transition-colors"
                                    style={{
                                        color: groupByCampaign ? 'var(--accent-emphasis)' : 'var(--text-secondary)',
                                        background: groupByCampaign ? 'var(--accent-muted)' : 'transparent',
                                        border: `1px solid ${groupByCampaign ? 'var(--border-accent)' : 'var(--border-default)'}`,
                                    }}
                                    title={groupByCampaign ? 'Ungroup the list' : 'Group the list by campaign'}
                                    aria-pressed={groupByCampaign}
                                >
                                    <Layers size={13} strokeWidth={1.75} />
                                    Group
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {feedStale && (
                    <button
                        type="button"
                        onClick={() => void reloadInboxToCurrentDepth()}
                        className="flex h-9 shrink-0 items-center justify-center gap-1.5 px-3 text-[12px] font-medium"
                        style={{
                            color: 'var(--accent-emphasis)',
                            background: 'var(--accent-muted)',
                            borderBottom: '1px solid var(--border-accent)',
                        }}
                    >
                        <RefreshCw size={12} /> New inbox activity · Refresh conversations
                    </button>
                )}

                {/* List */}
                <div
                    ref={listScrollRef}
                    onScroll={handleListScroll}
                    className="min-h-0 flex-1 overflow-y-auto"
                    aria-busy={listStatus === 'loading' || isLoadingMore}
                >
                    {listStatus === 'loading' && items.length === 0 && (
                        <div className="flex flex-col gap-px p-2">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div
                                    key={i}
                                    className="h-[68px] animate-pulse rounded-lg"
                                    style={{ background: 'var(--surface-tertiary)' }}
                                />
                            ))}
                        </div>
                    )}

                    {listStatus === 'error' && (
                        <div
                            className="m-3 flex items-center justify-between gap-2 rounded-lg p-3"
                            style={{ background: 'var(--bg-danger)', color: 'var(--fg-danger)' }}
                        >
                            <span className="flex items-center gap-2 text-[12px]">
                                <AlertCircle size={14} strokeWidth={2} />
                                {listError}
                            </span>
                            <button onClick={loadInbox} className="flex items-center gap-1 text-[12px] font-medium">
                                <RefreshCw size={12} strokeWidth={2} /> Retry
                            </button>
                        </div>
                    )}

                    {listStatus === 'loaded' && items.length === 0 && (
                        <div className="flex flex-col items-center px-6 py-16 text-center">
                            <div
                                className="mb-3 flex h-10 w-10 items-center justify-center rounded-full"
                                style={{ background: 'var(--surface-tertiary)', color: 'var(--text-tertiary)' }}
                            >
                                <InboxIcon size={18} strokeWidth={1.75} />
                            </div>
                            <p className="text-[14px] font-medium" style={{ color: 'var(--text-primary)' }}>
                                You’re all caught up
                            </p>
                            <p className="mt-1 text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                                {filter === 'needs_action'
                                    ? 'Nothing needs your attention right now.'
                                    : 'No conversations match this filter.'}
                            </p>
                        </div>
                    )}

                    {listRows.length > 0 && (
                        <div
                            style={{
                                height: listVirtualizer.getTotalSize(),
                                position: 'relative',
                                width: '100%',
                            }}
                        >
                            {listVirtualizer.getVirtualItems().map((virtualRow) => {
                                const row = listRows[virtualRow.index];
                                if (!row) return null;
                                const stickyHeader =
                                    row.kind === 'campaign' && virtualRow.index === activeCampaignHeaderIndex;
                                return (
                                    <div
                                        key={virtualRow.key}
                                        ref={listVirtualizer.measureElement}
                                        data-index={virtualRow.index}
                                        data-inbox-row-kind={row.kind}
                                        style={{
                                            position: stickyHeader ? 'sticky' : 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            zIndex: stickyHeader ? 2 : row.kind === 'campaign' ? 1 : undefined,
                                            transform: stickyHeader ? undefined : `translateY(${virtualRow.start}px)`,
                                        }}
                                    >
                                        {row.kind === 'conversation' ? (
                                            <InboxListItem
                                                item={row.item}
                                                active={row.item._id === selectedId}
                                                onClick={() => selectThread(row.item._id)}
                                            />
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => toggleGroup(row.groupId)}
                                                className="flex w-full items-center gap-1.5 px-3 py-2 text-left"
                                                style={{
                                                    background: 'var(--surface-secondary)',
                                                    borderBottom: '1px solid var(--border-muted)',
                                                }}
                                                aria-expanded={!row.collapsed}
                                            >
                                                <ChevronRight
                                                    size={13}
                                                    strokeWidth={2}
                                                    style={{
                                                        color: 'var(--text-tertiary)',
                                                        transform: row.collapsed ? 'none' : 'rotate(90deg)',
                                                        transition: 'transform 120ms',
                                                    }}
                                                />
                                                <Megaphone
                                                    size={12}
                                                    strokeWidth={1.75}
                                                    style={{ color: 'var(--text-tertiary)' }}
                                                />
                                                <span
                                                    className="min-w-0 flex-1 truncate text-[12px] font-semibold"
                                                    style={{ color: 'var(--text-secondary)' }}
                                                >
                                                    {row.name}
                                                </span>
                                                <span
                                                    className="text-[11px] tabular-nums"
                                                    style={{ color: 'var(--text-tertiary)' }}
                                                >
                                                    {row.count}
                                                </span>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {items.length > 0 && (
                        <div
                            className="flex min-h-12 items-center justify-center px-3 py-2 text-[11px]"
                            style={{ color: loadMoreError ? 'var(--fg-danger)' : 'var(--text-tertiary)' }}
                        >
                            {feedStale ? (
                                <button
                                    type="button"
                                    onClick={() => void reloadInboxToCurrentDepth()}
                                    className="flex items-center gap-1.5 font-medium"
                                >
                                    <RefreshCw size={12} /> Refresh before loading more
                                </button>
                            ) : isLoadingMore ? (
                                <span className="flex items-center gap-1.5">
                                    <Loader2 size={13} className="animate-spin" /> Loading more conversations…
                                </span>
                            ) : loadMoreError ? (
                                <button
                                    type="button"
                                    onClick={() => void loadMoreInbox()}
                                    className="flex items-center gap-1.5 font-medium"
                                >
                                    <RefreshCw size={12} /> {loadMoreError} — Retry
                                </button>
                            ) : hasMore ? (
                                <button
                                    type="button"
                                    onClick={() => void loadMoreInbox()}
                                    className="font-medium hover:underline"
                                >
                                    {items.length.toLocaleString()} of {listTotal.toLocaleString()} loaded · Load more
                                </button>
                            ) : (
                                <span>All {listTotal.toLocaleString()} conversations loaded</span>
                            )}
                        </div>
                    )}
                </div>
            </aside>

            <PaneDivider onDrag={resizeList} ariaLabel="Resize the conversation list" />

            {/* ───────────── Pane 2: thread ───────────── */}
            <section className="flex min-h-0 min-w-0 flex-1 flex-col">
                {!selectedId ? (
                    <EmptyThread />
                ) : threadStatus === 'loading' && !thread ? (
                    <div className="flex flex-1 items-center justify-center">
                        <Loader2 size={20} className="animate-spin" style={{ color: 'var(--text-tertiary)' }} />
                    </div>
                ) : threadStatus === 'error' ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-3">
                        <span className="flex items-center gap-2 text-[13px]" style={{ color: 'var(--fg-danger)' }}>
                            <AlertCircle size={15} strokeWidth={2} /> {threadError}
                        </span>
                        <button
                            onClick={() => selectedId && loadThread(selectedId)}
                            className="flex items-center gap-1 text-[12px] font-medium"
                            style={{ color: 'var(--accent)' }}
                        >
                            <RefreshCw size={12} strokeWidth={2} /> Retry
                        </button>
                    </div>
                ) : thread ? (
                    <>
                        <ThreadHeader
                            thread={thread}
                            onDone={handleDone}
                        />
                        {/* Channel tabs — only when the thread spans channels. A tab
                            also points the composer at that channel. */}
                        {threadChannels.length > 1 && (
                            <ChannelTabs
                                channels={threadChannels}
                                active={timelineChannel}
                                replyAccounts={thread.replyAccounts}
                                onSelect={(t) => {
                                    setTimelineChannel(t);
                                    if (t !== 'all') setReplyChannel(t);
                                }}
                            />
                        )}
                        <div ref={timelineScrollRef} className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                            <ThreadTimeline thread={thread} channel={timelineChannel} />
                            <div ref={timelineEndRef} />
                        </div>
                        {/* Send failure — surfaced so a refused/failed send is never silent (§9). */}
                        {sendError && (
                            <div
                                className="mx-4 mt-3 flex items-start gap-2 rounded-lg px-3 py-2 text-[12.5px]"
                                role="alert"
                                style={{
                                    background: 'var(--surface-danger, rgba(220, 38, 38, 0.08))',
                                    border: '1px solid var(--border-danger, rgba(220, 38, 38, 0.35))',
                                    color: 'var(--fg-danger, #b42318)',
                                }}
                            >
                                <AlertCircle size={14} strokeWidth={2} className="mt-px shrink-0" />
                                <span className="flex-1">
                                    Couldn’t send your reply: {sendError}. Your message was kept — try again.
                                </span>
                                <button
                                    onClick={() => setSendError(null)}
                                    className="shrink-0 opacity-70 hover:opacity-100"
                                    aria-label="Dismiss"
                                >
                                    <X size={13} strokeWidth={2} />
                                </button>
                            </div>
                        )}
                        {/* Sender-down strip (Stage 1): this channel's account is
                            disconnected — replies can't send until it's reconnected. */}
                        {replyChannelDown && (
                            <div
                                className="mx-4 mt-2 flex items-center gap-2 rounded-lg px-3 py-2 text-[12px]"
                                style={{ background: 'var(--bg-warning, #FBF3E4)', color: 'var(--fg-warning, #8A6116)' }}
                            >
                                <AlertTriangle size={13} strokeWidth={1.75} />
                                <span>
                                    The account this thread uses is disconnected — replies can’t be sent.{' '}
                                    <a href="/v2/outreach/settings" style={{ textDecoration: 'underline' }}>
                                        Reconnect it →
                                    </a>
                                </span>
                            </div>
                        )}
                        {/* key per thread: a fresh composer for each candidate, so a draft
                            (incl. an AI suggestion) never bleeds into the next conversation (§9). */}
                        <ReplyComposer
                            key={selectedId}
                            ref={composerRef}
                            channels={threadChannels}
                            channel={activeReplyChannel}
                            onChannelChange={handleComposerChannel}
                            replyAccounts={thread.replyAccounts}
                            bookingLink={bookingLink}
                            candidateFirstName={thread.candidate?.firstName}
                            sending={sending}
                            disabled={replyChannelDown}
                            canDraft={canDraft}
                            onDraft={handleDraft}
                            onSend={handleSend}
                        />
                    </>
                ) : null}
            </section>

            {/* ───────────── Pane 3: context ───────────── */}
            {thread && (
                <>
                    <PaneDivider onDrag={(dx) => resizeContext(-dx)} ariaLabel="Resize the candidate panel" />
                    <ContextPane thread={thread} width={contextWidth} />
                </>
            )}
        </div>
    );
}

/* ─────────────────────────── channel tabs ─────────────────────────── */

/** History switcher for multi-channel threads: All + one tab per channel the
 *  conversation has used. Mirrors (and steers) the composer's channel. */
function ChannelTabs({
    channels,
    active,
    replyAccounts,
    onSelect,
}: {
    channels: StepChannel[];
    active: StepChannel | 'all';
    replyAccounts: ReplyAccounts;
    onSelect: (t: StepChannel | 'all') => void;
}) {
    const tabs: ('all' | StepChannel)[] = ['all', ...channels];
    return (
        <div
            className="flex shrink-0 items-center gap-1 px-6 py-1.5"
            style={{ borderBottom: '1px solid var(--border-muted)', background: 'var(--surface-primary)' }}
            role="tablist"
            aria-label="Conversation channel"
        >
            {tabs.map((t) => {
                const isActive = active === t;
                const Icon = t === 'all' ? Layers : getInboxIcon(t, replyAccounts[t]?.provider);
                return (
                    <button
                        key={t}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => onSelect(t)}
                        className="flex h-7 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-medium transition-colors"
                        style={{
                            background: isActive ? 'var(--surface-active)' : 'transparent',
                            color: isActive ? 'var(--text-primary)' : 'var(--text-tertiary)',
                        }}
                        title={t === 'all' ? 'Show every channel' : `Show only the ${CHANNEL_FILTER_LABEL[t]} history`}
                    >
                        <Icon size={12} strokeWidth={1.75} />
                        {CHANNEL_FILTER_LABEL[t]}
                    </button>
                );
            })}
        </div>
    );
}

/* ─────────────────────────── list item ─────────────────────────── */

function InboxListItem({ item, active, onClick }: { item: InboxItem; active: boolean; onClick: () => void }) {
    const name = item.candidate?.name || 'Unknown candidate';
    // Only the clear yes/no reads as a chip; softer classes (unclear, maybe,
    // asks_*, etc.) stay quiet to keep the list calm.
    const cls =
        item.latestClassification === 'interested' || item.latestClassification === 'not_interested'
            ? REPLY_CLASS_META[item.latestClassification]
            : null;
    const campaign = item.campaigns[0];
    const interest = item.interestLevel ? INTEREST_META[item.interestLevel] : null;
    const waiting = waitingFor(item);

    return (
        <button
            onClick={onClick}
            className="flex w-full gap-3 px-3 py-3 text-left transition-colors"
            style={{
                background: active ? 'var(--surface-active)' : 'transparent',
                borderBottom: '1px solid var(--border-muted)',
                borderLeft: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
            }}
            onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = 'var(--surface-hover)';
            }}
            onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = 'transparent';
            }}
        >
            <div className="relative shrink-0">
                <Avatar initials={initialsOf(name)} src={item.candidate?.avatarUrl || undefined} size="lg" />
                {item.needsReply && (
                    <span
                        className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full"
                        style={{ background: 'var(--accent)', border: '2px solid var(--surface-primary)' }}
                    />
                )}
            </div>

            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                    {interest && (
                        <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ background: interest.dot }}
                            title={`${interest.label}${item.interestSource === 'manual' ? ' (marked by you)' : ''}`}
                        />
                    )}
                    <span className="flex-1 truncate text-[13px] font-medium" style={{ color: 'var(--text-primary)' }}>
                        {name}
                    </span>
                    {item.channels.map((ch) => {
                        const provider = item.preview?.channel === ch ? item.preview.provider : null;
                        const Icon = getInboxIcon(ch, provider);
                        return <Icon key={ch} size={12} strokeWidth={1.75} style={{ color: 'var(--text-tertiary)' }} />;
                    })}
                    <span className="shrink-0 text-[11px] tabular-nums" style={{ color: 'var(--text-tertiary)' }}>
                        {relativeTime(item.lastActivityAt)}
                    </span>
                </div>

                {item.preview && (
                    <p className="mt-0.5 truncate text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                        {item.preview.direction === 'out' ? '↩ ' : ''}
                        {item.preview.body || '—'}
                    </p>
                )}

                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {cls && <Badge variant={cls.variant}>{cls.label}</Badge>}
                    {item.escalated && (
                        <span
                            title="Needs your judgment"
                            className="inline-flex items-center"
                            style={{ color: 'var(--fg-warning)' }}
                        >
                            <ShieldAlert size={12} strokeWidth={2} />
                        </span>
                    )}
                    {/* The classifier's recommended next step — coral when primary */}
                    {item.needsReply && item.suggestedAction && (
                        <span
                            className="inline-flex max-w-[150px] items-center gap-0.5 truncate text-[11px] font-medium"
                            style={{
                                color: item.suggestedAction.primary ? 'var(--accent-emphasis)' : 'var(--text-secondary)',
                            }}
                        >
                            → {item.suggestedAction.label}
                        </span>
                    )}
                    {/* Aging: how long this reply has waited on a human */}
                    {waiting && (
                        <span
                            className="inline-flex items-center gap-1 text-[11px] tabular-nums"
                            style={{ color: waiting.warn ? 'var(--fg-warning)' : 'var(--text-tertiary)' }}
                            title="Time since the candidate's reply"
                        >
                            <Clock size={10} strokeWidth={1.75} /> {waiting.label}
                        </span>
                    )}
                    {campaign && (
                        <span
                            className="inline-flex max-w-[140px] items-center gap-1 truncate text-[11px]"
                            style={{ color: 'var(--text-tertiary)' }}
                        >
                            <Megaphone size={10} strokeWidth={1.75} /> {campaign.name}
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
}

/* ─────────────────────────── thread header ─────────────────────────── */

/** lemlist-style interest marker: three dots (hot / warm / dead). Read-only display
 *  — only the AI can modify interest levels. */
function InterestPicker({
    level,
    source,
}: {
    level: InterestLevel | null;
    source: 'ai' | 'manual' | null;
}) {
    return (
        <div
            className="flex h-8 items-center gap-0.5 rounded-lg px-1"
            style={{ border: '1px solid var(--border-default)', background: 'var(--surface-primary)' }}
            title={level ? `${INTEREST_META[level].label} (${source === 'manual' ? 'marked by you' : 'AI'}) — AI determines interest` : 'Interest determined by AI'}
        >
            {INTEREST_ORDER.map((l) => {
                const meta = INTEREST_META[l];
                const active = level === l;
                return (
                    <div
                        key={l}
                        className="flex h-6 items-center gap-1.5 rounded-md px-1.5"
                        style={{ background: active ? 'var(--surface-active)' : 'transparent' }}
                    >
                        <span
                            className="h-2.5 w-2.5 rounded-full"
                            style={{ background: meta.dot, opacity: active || !level ? 1 : 0.35 }}
                        />
                        {active && (
                            <span className="text-[11px] font-medium" style={{ color: 'var(--text-primary)' }}>
                                {meta.label}
                            </span>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function ThreadHeader({
    thread,
    onDone,
}: {
    thread: ThreadResponse;
    onDone: () => void;
}) {
    const c = thread.candidate;
    const conv = thread.conversation;
    const name = c?.name || 'Unknown candidate';
    return (
        <header
            className="flex h-16 shrink-0 items-center gap-3 px-6"
            style={{ borderBottom: '1px solid var(--border-default)', background: 'var(--surface-primary)' }}
        >
            <Avatar initials={initialsOf(name)} src={c?.avatarUrl || undefined} size="lg" />
            <div className="min-w-0 flex-1">
                <h2 className="truncate text-[15px] font-medium" style={{ color: 'var(--text-primary)' }}>
                    {name}
                </h2>
                {(c?.title || c?.company) && (
                    <p className="truncate text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                        {[c?.title, c?.company].filter(Boolean).join(' · ')}
                    </p>
                )}
            </div>
            {/* Triage cluster (§9): interest (AI-only) · done */}
            <div className="flex shrink-0 items-center gap-2">
                <InterestPicker level={conv.interestLevel} source={conv.interestSource} />
                {(conv.needsReply || conv.escalated) && (
                    <button
                        type="button"
                        onClick={onDone}
                        className="flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-medium transition-colors"
                        style={{
                            border: '1px solid var(--border-default)',
                            background: 'var(--surface-primary)',
                            color: 'var(--text-secondary)',
                        }}
                        title="Clear from the needs-action queue (the thread stays in All)"
                    >
                        <CheckCircle2 size={13} strokeWidth={1.75} />
                        Done
                    </button>
                )}
            </div>
        </header>
    );
}

/* ─────────────────────────── timeline ─────────────────────────── */

/* The thread reads as a cross-channel sequence: each outbound touch sits on a
 * channel-marked rail (icon chip + connector) with a "Day N · Channel" label and
 * a delivery-status pill, while the candidate's inbound replies break out to the
 * right as bubbles with their avatar. One glance tracks what we sent, on which
 * channel, when, what happened to it — and where the conversation now stands. */

// Per-channel rail styling. Each touch uses the actual sending provider's mark
// when the message carries one (Gmail/Outlook), with a neutral email envelope
// for legacy/unknown accounts. `ink` only colors generic glyphs; brand marks
// carry their own color and coral stays reserved for meaning.
const CHANNEL_RAIL_STYLE: Record<StepChannel, { chipBg: string; ink: string }> = {
    email: { chipBg: 'var(--surface-primary)', ink: 'var(--text-secondary)' },
    linkedin: { chipBg: 'var(--surface-primary)', ink: '#0A66C2' },
    whatsapp: { chipBg: 'var(--surface-primary)', ink: '#1FA855' },
};

function channelRail(
    channel: StepChannel,
    direction: ThreadMessage['direction'],
    provider?: AccountProvider | null
) {
    const style = CHANNEL_RAIL_STYLE[channel];
    return { ...style, icon: getInboxMessageIcon(channel, direction, provider) };
}

// An outbound message's delivery state → a friendly, channel-aware pill. Mirrors
// the monotonic status ladder (status-rank.js: queued<sent<delivered<opened<clicked,
// terminal bounced/failed). `repliedTo` (a later inbound on the same channel) is
// the strongest signal and wins — shown green, like the reference's "Replied".
function deliveryStatusMeta(
    status: string,
    channel: StepChannel,
    repliedTo: boolean
): { label: string; variant: BadgeVariant } {
    if (repliedTo) return { label: 'Replied', variant: 'success' };
    switch (status) {
        case 'opened':
        case 'clicked':
            // "Opened" reads right for email; LinkedIn/WhatsApp say "Seen".
            return { label: channel === 'email' ? 'Opened' : 'Seen', variant: 'info' };
        case 'delivered':
            return { label: 'Delivered', variant: 'default' };
        case 'sent':
            return { label: 'Sent', variant: 'default' };
        case 'queued':
        case 'sending':
            return { label: 'Scheduled', variant: 'default' };
        case 'bounced':
            return { label: 'Bounced', variant: 'danger' };
        case 'failed':
            return { label: 'Failed', variant: 'danger' };
        default:
            return { label: 'Sent', variant: 'default' };
    }
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const msOf = (m: ThreadMessage): number => new Date(m.sentAt || m.receivedAt || m.createdAt).getTime();

// "Day N" of the sequence: calendar days since the first touch (day-of-first = 1).
// Rounded off start-of-day so it ignores send times / DST. Anchored to the whole
// thread so a channel-focused view keeps the same day numbers.
function sequenceDay(anchorMs: number, ms: number): number {
    const a = new Date(anchorMs);
    a.setHours(0, 0, 0, 0);
    const b = new Date(ms);
    b.setHours(0, 0, 0, 0);
    return Math.round((b.getTime() - a.getTime()) / MS_PER_DAY) + 1;
}

// A short, absolute timestamp for a message footer ("Jun 21, 2:14 PM"). On a
// history timeline you want the real time, not the list's relative age.
function messageTime(ms: number): string {
    if (Number.isNaN(ms)) return '';
    return new Date(ms).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

// Which outbound messages drew a reply: an inbound on the same channel arriving
// before the next outbound on that channel marks the preceding outbound "Replied".
// One pass over the time-sorted thread, tracking the open send per channel.
function repliedOutboundIds(messages: ThreadMessage[]): Set<string> {
    const replied = new Set<string>();
    const openByChannel = new Map<StepChannel, string>();
    for (const m of messages) {
        const ch = (m.channel || 'email') as StepChannel;
        if (m.direction === 'out') {
            openByChannel.set(ch, m._id);
        } else if (m.direction === 'in') {
            const open = openByChannel.get(ch);
            if (open) {
                replied.add(open);
                openByChannel.delete(ch);
            }
        }
    }
    return replied;
}

function ThreadTimeline({ thread, channel }: { thread: ThreadResponse; channel: StepChannel | 'all' }) {
    // Channel focus (multi-channel threads): show only that channel's history.
    const messages =
        channel === 'all' ? thread.messages : thread.messages.filter((m) => (m.channel || 'email') === channel);

    // Day numbers + the reply map anchor to the WHOLE thread so a channel-focused
    // view keeps stable "Day N" labels and accurate "Replied" pills.
    const anchorMs = useMemo(
        () => (thread.messages.length ? Math.min(...thread.messages.map(msOf)) : Date.now()),
        [thread.messages]
    );
    const repliedIds = useMemo(() => repliedOutboundIds(thread.messages), [thread.messages]);

    if (!messages.length) {
        return (
            <div className="flex flex-col items-center py-12 text-center">
                <p className="text-[13px]" style={{ color: 'var(--text-tertiary)' }}>
                    {channel === 'all'
                        ? 'No messages in this thread yet.'
                        : `No ${CHANNEL_FILTER_LABEL[channel]} messages yet — other channels are hidden.`}
                </p>
            </div>
        );
    }

    const candidate = thread.candidate;

    return (
        <div className="mx-auto max-w-2xl">
            {messages.map((m, i) => {
                const ch = (m.channel || 'email') as StepChannel;
                const cls = m.classification ? REPLY_CLASS_META[m.classification] : null;
                const account = m.accountId && typeof m.accountId === 'object' ? m.accountId : null;

                /* ── inbound: the candidate's reply — right-aligned with their
                      avatar, breaking out of the left sequence rail (per reference) ── */
                if (m.direction === 'in') {
                    // The candidate's reply — show only its source channel + time
                    // (who it's from is clear from the thread). Interest is flagged
                    // only on a clear yes/no; softer classes stay quiet.
                    const inRail = channelRail(ch, m.direction, account?.provider);
                    const InIcon = inRail.icon;
                    const showTag = m.classification === 'interested' || m.classification === 'not_interested';
                    return (
                        <div key={m._id} className="flex justify-end gap-2.5 pb-5">
                            <div className="flex min-w-0 max-w-[82%] flex-col items-end">
                                <div className="mb-1 flex items-center gap-1.5">
                                    {showTag && cls && <Badge variant={cls.variant}>{cls.label}</Badge>}
                                    <span
                                        className="inline-flex items-center gap-1 text-[11px]"
                                        style={{ color: 'var(--text-tertiary)' }}
                                    >
                                        <InIcon size={11} strokeWidth={2} style={{ color: inRail.ink }} />
                                        {CHANNEL_FILTER_LABEL[ch]} · {relativeTime(m.sentAt || m.receivedAt || m.createdAt)}
                                    </span>
                                </div>
                                <div
                                    className="min-w-0 break-words rounded-2xl rounded-tr-md px-4 py-2.5"
                                    style={{
                                        background: 'var(--surface-secondary)',
                                        border: '1px solid var(--border-default)',
                                    }}
                                >
                                    <MessageBody message={m} />
                                </div>
                            </div>
                            <Avatar
                                initials={initialsOf(candidate?.name)}
                                src={candidate?.avatarUrl || undefined}
                                size="md"
                                className="mt-6 shrink-0"
                            />
                        </div>
                    );
                }

                /* ── outbound: a sequence / recruiter touch on the channel rail ── */
                const rail = channelRail(ch, m.direction, account?.provider);
                const Icon = rail.icon;
                const channelLabel =
                    ch === 'linkedin' && m.linkedinAction === 'inmail'
                        ? 'LinkedIn InMail'
                        : CHANNEL_FILTER_LABEL[ch];
                const day = sequenceDay(anchorMs, msOf(m));
                const status = deliveryStatusMeta(m.status, ch, repliedIds.has(m._id));
                // Attribution: a manual reply carries `authoredBy`; an engine /
                // sequence send doesn't — so the recruiter can tell what the
                // automation sent on their behalf from what they sent themselves.
                const sentBy = m.authoredBy ? 'You' : 'Automated';
                const accountLabel = account?.displayName || account?.emailAddress || '';
                // The rail connects consecutive outbound touches: each send draws a
                // line from its chip down to the next, so the rail reads continuous
                // through the cards yet breaks cleanly around an inbound reply.
                const connectDown = i < messages.length - 1 && messages[i + 1].direction === 'out';

                return (
                    <div key={m._id} className="relative flex gap-3 pb-5">
                        {/* connector — anchored on the row (not the chip) so it spans
                            the gap into the next card for one unbroken rail. Starts at
                            the chip's bottom edge so it never cuts through the icon. */}
                        {connectDown && (
                            <span
                                aria-hidden
                                className="absolute left-[18px] top-[29px] bottom-0 w-px -translate-x-1/2"
                                style={{ background: 'var(--border-emphasis)' }}
                            />
                        )}
                        {/* rail: channel chip, nudged up to center on the Day · Channel label */}
                        <div className="relative flex w-9 shrink-0 justify-center">
                            <span
                                className="relative z-[1] flex h-9 w-9 items-center justify-center rounded-xl"
                                style={{
                                    marginTop: '-7px',
                                    background: rail.chipBg,
                                    border: '1px solid var(--border-default)',
                                    boxShadow: 'var(--shadow-sm)',
                                }}
                                title={channelLabel}
                            >
                                <Icon size={15} strokeWidth={1.9} style={{ color: rail.ink }} />
                            </span>
                        </div>

                        {/* card: Day · Channel · status, then body, then sender · time */}
                        <div className="min-w-0 flex-1">
                            <div className="mb-1.5 flex items-center gap-1.5">
                                <span className="text-[12px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                                    Day {day}
                                </span>
                                <span style={{ color: 'var(--text-quaternary)' }}>·</span>
                                <span className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                                    {channelLabel}
                                </span>
                                {m.aiGenerated && <Badge variant="accent">AI</Badge>}
                                <span className="flex-1" />
                                <Badge variant={status.variant}>{status.label}</Badge>
                            </div>
                            <div
                                className="min-w-0 break-words rounded-xl px-4 py-3"
                                style={{
                                    background: 'var(--surface-primary)',
                                    border: '1px solid var(--border-default)',
                                    boxShadow: 'var(--shadow-sm)',
                                }}
                            >
                                <MessageBody message={m} />
                            </div>
                            <p
                                className="mt-1.5 text-[11px]"
                                style={{ color: 'var(--text-tertiary)' }}
                                title={`${sentBy === 'You' ? 'Sent by you' : 'Sent automatically'}${
                                    accountLabel ? ` from ${accountLabel}` : ''
                                } · ${messageTime(msOf(m))}`}
                            >
                                <span style={{ color: 'var(--text-secondary)' }}>{sentBy}</span> ·{' '}
                                {relativeTime(m.sentAt || m.receivedAt || m.createdAt)}
                            </p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/* ─────────────────────────── context pane ─────────────────────────── */

function ContextPane({ thread, width }: { thread: ThreadResponse; width: number }) {
    const c = thread.candidate;
    const conv = thread.conversation;
    const interest = conv.interestLevel ? INTEREST_META[conv.interestLevel] : null;

    return (
        <aside
            className="flex min-h-0 shrink-0 flex-col overflow-y-auto"
            style={{ width, borderLeft: '1px solid var(--border-default)', background: 'var(--surface-primary)' }}
        >
            {/* Candidate profile — who they are + where they work, with the
                conversation status shown read-only (it follows the thread). */}
            <div className="p-4" style={{ borderBottom: '1px solid var(--border-muted)' }}>
                <div className="flex items-center gap-2.5">
                    <Avatar initials={initialsOf(c?.name)} src={c?.avatarUrl || undefined} size="lg" />
                    <div className="min-w-0">
                        <p className="truncate text-[14px] font-medium" style={{ color: 'var(--text-primary)' }}>
                            {c?.name || 'Unknown'}
                        </p>
                        {(c?.title || c?.company) && (
                            <p className="truncate text-[12px]" style={{ color: 'var(--text-secondary)' }}>
                                {[c?.title, c?.company].filter(Boolean).join(' · ')}
                            </p>
                        )}
                    </div>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                    <Badge variant="default">{CONVERSATION_STATUS_LABELS[conv.status] || conv.status}</Badge>
                    {interest && (
                        <span
                            className="inline-flex items-center gap-1.5 text-[11px] font-medium"
                            style={{ color: 'var(--text-secondary)' }}
                            title={conv.interestSource === 'manual' ? 'Marked by you' : 'AI rollup'}
                        >
                            <span className="h-2 w-2 rounded-full" style={{ background: interest.dot }} />
                            {interest.label}
                        </span>
                    )}
                </div>
                <div className="mt-3 flex flex-col gap-1.5">
                    {c?.company && <ContextRow icon={Building2} text={`Works at ${c.company}`} />}
                    {c?.location && <ContextRow icon={MapPin} text={c.location} />}
                    {c?._id && <EmailContextRow key={c._id} candidate={c} enrollments={thread.enrollments} />}
                    {c?.phone && <ContextRow icon={Phone} text={c.phone} />}
                    {c?.linkedInUrl && (
                        <ContextRow icon={CHANNEL_ICON.linkedin} text="LinkedIn profile" href={c.linkedInUrl} />
                    )}
                    {c?.websiteUrl && <ContextRow icon={Globe} text="Website" href={c.websiteUrl} />}
                    {c?.source && <ContextRow icon={UserIcon} text={`Source: ${c.source}`} />}
                </div>
            </div>

            {/* Campaign context */}
            {thread.campaigns.length > 0 && (
                <div className="p-4" style={{ borderBottom: '1px solid var(--border-muted)' }}>
                    <p className="mb-2 text-[12px] font-medium" style={{ color: 'var(--text-tertiary)' }}>
                        Campaigns
                    </p>
                    <div className="flex flex-col gap-2">
                        {thread.campaigns.map((cp) => (
                            <div
                                key={cp._id}
                                className="flex flex-col gap-2 rounded-lg p-2.5"
                                style={{ background: 'var(--surface-secondary)' }}
                            >
                                <div className="flex items-center gap-2">
                                    <Megaphone size={13} strokeWidth={1.75} style={{ color: 'var(--text-tertiary)' }} />
                                    <span className="flex-1 truncate text-[12px]" style={{ color: 'var(--text-primary)' }}>
                                        {cp.name}
                                    </span>
                                    {cp.status && <Badge variant="default">{cp.status}</Badge>}
                                </div>
                            </div>
                        ))}
                    </div>
                    {thread.enrollments.length > 0 && (
                        <div className="mt-2 flex flex-col gap-1">
                            {thread.enrollments.map((e) => (
                                <p
                                    key={e._id}
                                    className="flex items-center gap-1.5 text-[11px]"
                                    style={{ color: 'var(--text-tertiary)' }}
                                >
                                    <Clock size={10} strokeWidth={1.75} />
                                    Automation: <span style={{ color: 'var(--text-secondary)' }}>{e.status}</span>
                                    {e.stopReason ? ` (${e.stopReason})` : ''}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Activity log — sends / replies / bounces, newest first. The
                message count keys a refresh so a just-sent reply shows up. */}
            <ThreadActivityLog threadId={String(conv._id)} refreshKey={thread.messages.length} />
        </aside>
    );
}

/* ─────────────────────────── activity log ─────────────────────────── */

function activityMeta(e: ThreadActivityEntry): { label: string; icon: typeof Mail; color: string } {
    const channelIcon = e.channel ? CHANNEL_ICON[e.channel] : Mail;
    switch (e.type) {
        case 'message.sent':
            return e.manual
                ? { label: 'You replied', icon: channelIcon, color: 'var(--text-secondary)' }
                : { label: 'Outreach sent', icon: channelIcon, color: 'var(--text-secondary)' };
        case 'message.received':
            return { label: 'Candidate replied', icon: channelIcon, color: 'var(--accent-emphasis)' };
        case 'email.bounce':
            return { label: 'Email bounced', icon: AlertCircle, color: 'var(--fg-danger)' };
        case 'email.failed':
            return { label: 'Email failed to send', icon: AlertCircle, color: 'var(--fg-danger)' };
        case 'relation.update':
            return { label: 'LinkedIn connection updated', icon: CHANNEL_ICON.linkedin, color: 'var(--text-secondary)' };
        case 'account.status':
            return { label: 'Sender account status changed', icon: AlertCircle, color: 'var(--fg-warning)' };
        case 'message.status':
        default:
            return { label: 'Delivery status updated', icon: Clock, color: 'var(--text-secondary)' };
    }
}

/** The thread's event feed (sends / replies / bounces) for the context pane.
 *  Loads lazily per thread; `refreshKey` bumps when the timeline grows. */
function ThreadActivityLog({ threadId, refreshKey }: { threadId: string; refreshKey: number }) {
    const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
    const [events, setEvents] = useState<ThreadActivityEntry[]>([]);
    const [startedAt, setStartedAt] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        setStatus('loading');
        inboxApi
            .activity(threadId)
            .then(({ data }) => {
                if (cancelled) return;
                setEvents(data.events || []);
                setStartedAt(data.startedAt || null);
                setStatus('loaded');
            })
            .catch(() => {
                if (!cancelled) setStatus('error');
            });
        return () => {
            cancelled = true;
        };
    }, [threadId, refreshKey]);

    return (
        <div className="p-4">
            <p
                className="mb-2 flex items-center gap-1.5 text-[12px] font-medium"
                style={{ color: 'var(--text-tertiary)' }}
            >
                <Activity size={12} strokeWidth={1.75} /> Activity
            </p>

            {status === 'loading' && (
                <div className="flex flex-col gap-1.5">
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className="h-4 animate-pulse rounded"
                            style={{ background: 'var(--surface-tertiary)' }}
                        />
                    ))}
                </div>
            )}

            {status === 'error' && (
                <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                    Couldn’t load the activity feed.
                </p>
            )}

            {status === 'loaded' && (
                <div className="flex flex-col gap-2">
                    {events.length === 0 && (
                        <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                            No activity recorded yet.
                        </p>
                    )}
                    {events.map((e) => {
                        const meta = activityMeta(e);
                        const Icon = meta.icon;
                        return (
                            <div key={e._id} className="flex items-start gap-2">
                                <Icon
                                    size={12}
                                    strokeWidth={1.75}
                                    className="mt-0.5 shrink-0"
                                    style={{ color: meta.color }}
                                />
                                <span
                                    className="min-w-0 flex-1 text-[12px] leading-snug"
                                    style={{ color: 'var(--text-secondary)' }}
                                >
                                    {meta.label}
                                    {e.channel && (
                                        <span style={{ color: 'var(--text-tertiary)' }}>
                                            {' '}
                                            · {CHANNEL_FILTER_LABEL[e.channel]}
                                        </span>
                                    )}
                                </span>
                                <span
                                    className="shrink-0 text-[11px] tabular-nums"
                                    style={{ color: 'var(--text-tertiary)' }}
                                >
                                    {relativeTime(e.at)}
                                </span>
                            </div>
                        );
                    })}
                    {startedAt && (
                        <div className="flex items-start gap-2">
                            <Clock
                                size={12}
                                strokeWidth={1.75}
                                className="mt-0.5 shrink-0"
                                style={{ color: 'var(--text-tertiary)' }}
                            />
                            <span className="min-w-0 flex-1 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
                                Conversation started
                            </span>
                            <span
                                className="shrink-0 text-[11px] tabular-nums"
                                style={{ color: 'var(--text-tertiary)' }}
                            >
                                {relativeTime(startedAt)}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function ContextRow({ icon: Icon, text, href }: { icon: typeof Mail; text: string; href?: string }) {
    const content = (
        <span
            className="flex items-center gap-2 truncate text-[12px]"
            style={{ color: href ? 'var(--accent)' : 'var(--text-secondary)' }}
        >
            <Icon size={13} strokeWidth={1.75} className="shrink-0" style={{ color: 'var(--text-tertiary)' }} />
            <span className="truncate">{text}</span>
        </span>
    );
    return href ? (
        <a href={href} target="_blank" rel="noopener noreferrer">
            {content}
        </a>
    ) : (
        content
    );
}

/** The email row in the context pane. Shows the address + a deliverability badge
 *  when one's on file; otherwise a Find-email action that runs the prospect
 *  waterfall (Stage 3). The InboxCandidate._id is the personKey, NOT a prospect —
 *  the enrichable target is the thread's running enrollment (prospect row); the
 *  found email is appended to THAT prospect only (siblings in other campaigns
 *  enrich on their own — the attempts cache makes the second run free).
 *  Keyed by candidate id so local state resets when the thread changes. */
function EmailContextRow({ candidate, enrollments }: { candidate: InboxCandidate; enrollments: ThreadEnrollment[] }) {
    const [finding, setFinding] = useState(false);
    const [foundEmail, setFoundEmail] = useState<string | null>(null);
    const [foundVerification, setFoundVerification] = useState<EmailVerification | null>(null);
    const [note, setNote] = useState<string | null>(null);

    const email = foundEmail || candidate.email || null;
    const verification: EmailVerification | null = foundVerification || candidate.emailVerification || null;
    const target =
        enrollments.find((e) => e.status === 'active') ||
        enrollments.find((e) => e.status === 'paused') ||
        enrollments[0] ||
        null;

    if (email) {
        return (
            <div className="flex items-center gap-2">
                <ContextRow icon={Mail} text={email} />
                {verification && <VerificationBadge status={verification.status} provider={verification.provider} />}
            </div>
        );
    }
    if (!target) return null; // no prospect behind this thread → nothing to enrich

    async function findEmail() {
        if (!target || finding) return;
        setFinding(true);
        try {
            const { data } = await enrichmentApi.enrichProspect(target._id);
            if (data.deferred) {
                setNote('Email provider is temporarily unavailable — try again later.');
            } else if (data.found && data.appended !== 'duplicate_of_sibling' && (data.identity?.emails?.[0] || data.email)) {
                setFoundEmail(data.identity?.emails?.[0] || data.email);
                setFoundVerification(data.verification || null);
                setNote(null);
            } else if (data.found) {
                setNote('Found an email, but it already belongs to another contact in this campaign.');
            } else {
                setNote('No email found — try again later');
            }
        } catch (err) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            setNote(status === 403 ? 'You need outreach edit access to find emails' : 'Email lookup failed — try again');
        } finally {
            setFinding(false);
        }
    }

    return (
        <div className="flex items-center gap-2">
            <button
                type="button"
                onClick={() => void findEmail()}
                disabled={finding}
                title={note || 'Find and verify an email for this candidate'}
                className="flex items-center gap-2 text-[12px] font-medium transition-opacity hover:opacity-80 disabled:cursor-wait disabled:opacity-60"
                style={{ color: 'var(--accent-emphasis)' }}
            >
                {finding ? (
                    <Loader2 size={13} className="animate-spin" strokeWidth={1.75} />
                ) : (
                    <Search size={13} strokeWidth={1.75} />
                )}
                {note ? 'Retry find email' : 'Find email'}
            </button>
        </div>
    );
}


/* ─────────────────────────── empty thread ─────────────────────────── */

function EmptyThread() {
    return (
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
            <div
                className="mb-3 flex h-12 w-12 items-center justify-center rounded-full"
                style={{ background: 'var(--surface-tertiary)', color: 'var(--text-tertiary)' }}
            >
                <InboxIcon size={22} strokeWidth={1.5} />
            </div>
            <p className="text-[15px] font-medium" style={{ color: 'var(--text-primary)' }}>
                Select a conversation
            </p>
            <p className="mt-1 max-w-xs text-[13px]" style={{ color: 'var(--text-secondary)' }}>
                Replies from every job and campaign land here. Use j / k to move and r to reply.
            </p>
        </div>
    );
}
